import React, { useId } from "react";
import { CHART_COLORS } from "@/features/investors/components/charts/palette";

type Props = {
    values: number[];
    labels: string[];
    color?: string;
    height?: number;
    highlightLast?: boolean;
    formatValue: (value: number) => string;
    tipFor?: (index: number) => string;
};

export const Bars: React.FC<Props> = ({
    values,
    labels,
    color = CHART_COLORS.blue,
    height = 200,
    highlightLast = true,
    formatValue,
    tipFor,
}) => {
    const gradientId = useId();
    if (values.length === 0) return null;

    const W = 640;
    const H = height;
    const P = { t: 18, r: 14, b: 28, l: 14 };
    const iw = W - P.l - P.r;
    const ih = H - P.t - P.b;
    const max = Math.max(...values) * 1.14 || 1;
    const gap = iw / values.length;
    const barWidth = gap * 0.54;
    const gridLines = [0, 1, 2, 3].map((step) => P.t + (ih / 3) * step);

    return (
        <svg
            viewBox={`0 0 ${W} ${H}`}
            width="100%"
            preserveAspectRatio="xMidYMid meet"
            style={{ display: "block" }}
        >
            <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor={color} stopOpacity="0.7" />
                    <stop offset="1" stopColor={color} stopOpacity="0.32" />
                </linearGradient>
            </defs>
            {gridLines.map((gy) => (
                <line
                    key={gy}
                    x1={P.l}
                    y1={gy.toFixed(1)}
                    x2={W - P.r}
                    y2={gy.toFixed(1)}
                    stroke={CHART_COLORS.grid}
                    strokeWidth={1}
                />
            ))}
            {values.map((value, index) => {
                const barHeight = (value / max) * ih;
                const bx = P.l + gap * index + (gap - barWidth) / 2;
                const by = P.t + ih - barHeight;
                const isLast = highlightLast && index === values.length - 1;
                const tip =
                    tipFor !== undefined
                        ? tipFor(index)
                        : `<b>${labels[index]}</b><br>${formatValue(value)}`;
                return (
                    <rect
                        key={labels[index]}
                        x={bx.toFixed(1)}
                        y={by.toFixed(1)}
                        width={barWidth.toFixed(1)}
                        height={Math.max(barHeight, 1).toFixed(1)}
                        rx={5}
                        fill={isLast ? color : `url(#${gradientId})`}
                        opacity={isLast ? 1 : 0.92}
                        data-tip={tip}
                        style={{ cursor: "pointer", transition: "opacity .15s" }}
                    />
                );
            })}
            {labels.map((label, index) => (
                <text
                    key={label}
                    x={(P.l + gap * index + gap / 2).toFixed(1)}
                    y={H - 8}
                    fill={CHART_COLORS.axis}
                    fontSize={11}
                    fontWeight={500}
                    textAnchor="middle"
                >
                    {label}
                </text>
            ))}
        </svg>
    );
};
